import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { setAuthToken } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export function AuthCallback() {
  console.log("AuthCallback called");
  const [, setLocation] = useLocation();
  const { login } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const apiURL = import.meta.env.VITE_API_URL || 'http://localhost:4001';

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        console.log("Called")
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const provider = urlParams.get('provider');
        const error = urlParams.get('error');

        if (error) {
          setStatus('error');
          toast({
            title: "OAuth Error",
            description: "Failed to authenticate with OAuth provider",
            variant: "destructive",
          });
          setTimeout(() => setLocation('/'), 2000);
          return;
        }

        if (!token) {
          setStatus('error');
          toast({
            title: "Authentication Error",
            description: "No authentication token received",
            variant: "destructive",
          });
          setTimeout(() => setLocation('/'), 2000);
          return;
        }

        // Store the token
        setAuthToken(token);

        // Get user data from token
        const response = await fetch(apiURL + '/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to get user data');
        }

        const userData = await response.json();

        // Update auth context
        login(userData.email, ''); // Empty password for OAuth users

        setStatus('success');
        toast({
          title: "Login Successful",
          description: `Welcome back! You've been authenticated via ${provider}.`,
        });
        

        setTimeout(() => setLocation('/'), 1500);
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        toast({
          title: "Authentication Error",
          description: "Failed to complete OAuth authentication",
          variant: "destructive",
        });
        setTimeout(() => setLocation('/'), 2000);
      }
    };

    handleOAuthCallback();
  }, [setLocation, login, toast]);

  return (
    <div className="min-h-screen gcep-gradient-bg flex items-center justify-center p-4">
      <div className="gcep-card p-8 w-full max-w-md text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Completing Authentication
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Please wait while we complete your OAuth login...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Successful
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Redirecting you to the dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Redirecting you back to login...
            </p>
          </>
        )}
      </div>
    </div>
  );
} 